import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../api/apiService';
import './TablePages.css';

interface DistributorOption {
  id: number;
  distributor_agency_name: string;
  distributor_code: string;
}

interface DriverOption {
  id: number;
  driver_name: string;
  mobile_number?: string;
  distributor_id: number;
  distributor_name?: string;
}

const Orders: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [distributors, setDistributors] = useState<DistributorOption[]>([]);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [assigning, setAssigning] = useState<number | null>(null);
  const [assigningDriver, setAssigningDriver] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter ? `/admin/orders?status=${statusFilter}` : '/admin/orders';
      const { data } = await apiService.get(url);
      setList(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const loadDistributors = useCallback(async () => {
    try {
      const { data } = await apiService.get('/admin/distributors?status=approved');
      setDistributors(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadDrivers = useCallback(async () => {
    try {
      const { data } = await apiService.get('/admin/drivers');
      setDrivers(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    load();
    loadDistributors();
    loadDrivers();
  }, [load, loadDistributors, loadDrivers]);

  const handleAssignDistributor = async (orderId: number, distributorId: number) => {
    setAssigning(orderId);
    try {
      await apiService.put(`/admin/orders/${orderId}/assign-distributor`, { distributor_id: distributorId });
      load();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to assign');
    } finally {
      setAssigning(null);
    }
  };

  const handleAssignDriver = async (orderId: number, driverId: number) => {
    if (!driverId) return;
    setAssigningDriver(orderId);
    try {
      await apiService.put(`/admin/orders/${orderId}/assign-driver`, { driver_id: driverId });
      load();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to assign driver');
    } finally {
      setAssigningDriver(null);
    }
  };

  const formatDate = (s: string) => (s ? new Date(s).toLocaleString() : '-');

  return (
    <div className="table-page">
      <div className="table-toolbar">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="table-select"
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="accepted">Accepted</option>
          <option value="driver_assigned">Driver assigned</option>
          <option value="driver_started">Driver started</option>
          <option value="driver_reached">Driver reached</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <div className="table-toolbar-right">
          <button type="button" onClick={load} className="btn-primary">Refresh</button>
        </div>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Amount</th>
                <th>Payment type</th>
                <th>Payment status</th>
                <th>Status</th>
                <th>User</th>
                <th>Distributor</th>
                <th>Driver</th>
                <th>Assign distributor</th>
                <th>Assign driver</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => {
                const driversForOrder = row.distributor_id
                  ? drivers.filter((dr) => dr.distributor_id === row.distributor_id)
                  : drivers;
                return (
                <tr key={row.id}>
                  <td>{row.order_number}</td>
                  <td>₹{row.total_amount}</td>
                  <td>{row.payment_method ?? '-'}</td>
                  <td>{row.payment_status ?? '-'}</td>
                  <td><span className="badge">{row.order_status}</span></td>
                  <td>{row.user_name} ({row.user_mobile})</td>
                  <td>{row.distributor_name ?? '-'}</td>
                  <td>{row.driver_name ?? '-'}</td>
                  <td>
                    {!row.distributor_id ? (
                      <select
                        className="table-select"
                        style={{ marginRight: 8 }}
                        onChange={(e) => {
                          const id = Number(e.target.value);
                          if (id) handleAssignDistributor(row.id, id);
                        }}
                        disabled={assigning === row.id}
                      >
                        <option value="">Select...</option>
                        {distributors.map((d) => (
                          <option key={d.id} value={d.id}>{d.distributor_agency_name}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="badge badge-approved">Assigned</span>
                    )}
                  </td>
                  <td>
                    {row.driver_id ? (
                      <span className="badge badge-approved">Assigned</span>
                    ) : (
                      <select
                        className="table-select"
                        onChange={(e) => {
                          const id = Number(e.target.value);
                          if (id) handleAssignDriver(row.id, id);
                        }}
                        disabled={assigningDriver === row.id || driversForOrder.length === 0}
                      >
                        <option value="">Select driver...</option>
                        {driversForOrder.map((dr) => (
                          <option key={dr.id} value={dr.id}>{dr.driver_name} {dr.mobile_number ? `(${dr.mobile_number})` : ''}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>{formatDate(row.created_at)}</td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Orders;
